import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  ClipboardList,
  GraduationCap,
  Info,
} from "lucide-react";
import { API_BASE } from "../apiConfig";

const ICON_STROKE = 1.5;

const EMPTY_FORM = {
  program: "Bachelor",
  departmentId: "",
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  dateOfBirth: "",
  gender: "",
  address: "",
  // Honeypot — left blank by real visitors; bots that auto-fill every field trip it.
  website: "",
};

function validate(values) {
  const errors = {};
  if (!values.firstName.trim()) errors.firstName = "First name is required.";
  if (!values.lastName.trim()) errors.lastName = "Last name is required.";
  if (!values.departmentId) errors.departmentId = "Please select a department.";
  if (values.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email.trim())) {
    errors.email = "Please enter a valid email address.";
  }
  return errors;
}

const labelClass = "mb-1.5 block text-sm font-medium text-slate-700";
const inputClass =
  "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-maroon-600 focus:ring-2 focus:ring-maroon-600/20";
const errorClass = "mt-1 text-xs text-red-600";

export function OnlineRegistrationPage() {
  const [values, setValues] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [departments, setDepartments] = useState([]);
  const [deptLoading, setDeptLoading] = useState(false);
  const [deptError, setDeptError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setDeptLoading(true);
    setDeptError("");
    fetch(`${API_BASE}/api/public/departments?program=${encodeURIComponent(values.program)}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        if (cancelled) return;
        setDepartments(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!cancelled) {
          setDepartments([]);
          setDeptError("Could not load departments. Please try again shortly.");
        }
      })
      .finally(() => {
        if (!cancelled) setDeptLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [values.program]);

  const onChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "program" ? { departmentId: "" } : {}),
    }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validate(values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await fetch(`${API_BASE}/api/public/online-registration`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          departmentId: Number(values.departmentId),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setSubmitError(data.error || `Request failed (${res.status})`);
        return;
      }
      setSuccess(data.message || "Application submitted successfully.");
      setValues((prev) => ({ ...EMPTY_FORM, program: prev.program }));
    } catch {
      setSubmitError(
        "Network error. Check that the university API is running and CORS is allowed."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main id="main" className="mx-auto w-full max-w-6xl flex-1 px-4 py-6 sm:px-6 sm:py-8">
      <nav className="mb-6 text-sm text-slate-600">
        <Link to="/" className="font-medium text-maroon-700 hover:text-maroon-800">
          ← Back to home
        </Link>
      </nav>

      <section
        className="relative mb-8 overflow-hidden rounded-2xl border border-slate-200/90 bg-white p-5 shadow-soft sm:p-8"
        aria-labelledby="hero-heading"
      >
        <div
          className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-brand-500/15 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-maroon-600/15 blur-3xl"
          aria-hidden
        />

        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-5">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-maroon-700 to-maroon-900 text-white shadow-lg shadow-maroon-900/30 sm:h-14 sm:w-14">
            <GraduationCap className="h-7 w-7 sm:h-8 sm:w-8" strokeWidth={ICON_STROKE} aria-hidden />
          </span>
          <div className="min-w-0 flex-1">
            <h1 id="hero-heading" className="text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
              Apply for admission
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base">
              Fill in your details below and choose the program and department you would like to join.
              Our admissions office will review your application and contact you with next steps.
            </p>
          </div>
        </div>
      </section>

      <div className="grid gap-6 lg:grid-cols-3 lg:items-start">
      {success ? (
        <section className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-emerald-800 shadow-card lg:col-span-2">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0" strokeWidth={ICON_STROKE} aria-hidden />
            <div>
              <h2 className="text-base font-bold">Application submitted</h2>
              <p className="mt-1 text-sm">{success}</p>
              <button
                type="button"
                onClick={() => setSuccess(null)}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-1.5 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
              >
                Submit another application
              </button>
            </div>
          </div>
        </section>
      ) : (
        <section
          className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card sm:p-8 lg:col-span-2"
          aria-labelledby="form-heading"
        >
          <h2 id="form-heading" className="mb-5 flex items-center gap-2 text-lg font-bold text-slate-900">
            <ClipboardList className="h-5 w-5 text-maroon-700" strokeWidth={ICON_STROKE} aria-hidden />
            Applicant information
          </h2>

          <form onSubmit={onSubmit} noValidate className="grid gap-5">
            {/* Honeypot: hidden from real visitors and screen readers; bots that fill every input trip it. */}
            <div aria-hidden="true" className="absolute -left-[9999px] top-auto h-0 w-0 overflow-hidden">
              <label>
                Leave this field blank
                <input
                  type="text"
                  name="website"
                  value={values.website}
                  onChange={onChange}
                  tabIndex={-1}
                  autoComplete="off"
                />
              </label>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className={labelClass}>Program</span>
                <select name="program" value={values.program} onChange={onChange} className={inputClass}>
                  <option value="Bachelor">Bachelor Degree</option>
                  <option value="Masters">Masters Degree</option>
                </select>
              </label>

              <label className="block">
                <span className={labelClass}>Department</span>
                <select
                  name="departmentId"
                  value={values.departmentId}
                  onChange={onChange}
                  className={inputClass}
                  disabled={deptLoading}
                >
                  <option value="">
                    {deptLoading ? "Loading departments…" : "Select a department"}
                  </option>
                  {departments.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} ({d.code})
                    </option>
                  ))}
                </select>
                {errors.departmentId ? <p className={errorClass}>{errors.departmentId}</p> : null}
                {deptError ? <p className={errorClass}>{deptError}</p> : null}
              </label>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className={labelClass}>First name</span>
                <input
                  type="text"
                  name="firstName"
                  value={values.firstName}
                  onChange={onChange}
                  className={inputClass}
                  autoComplete="given-name"
                />
                {errors.firstName ? <p className={errorClass}>{errors.firstName}</p> : null}
              </label>

              <label className="block">
                <span className={labelClass}>Last name</span>
                <input
                  type="text"
                  name="lastName"
                  value={values.lastName}
                  onChange={onChange}
                  className={inputClass}
                  autoComplete="family-name"
                />
                {errors.lastName ? <p className={errorClass}>{errors.lastName}</p> : null}
              </label>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className={labelClass}>Email address</span>
                <input
                  type="email"
                  name="email"
                  value={values.email}
                  onChange={onChange}
                  className={inputClass}
                  autoComplete="email"
                  placeholder="you@example.com"
                />
                {errors.email ? <p className={errorClass}>{errors.email}</p> : null}
              </label>

              <label className="block">
                <span className={labelClass}>Phone number</span>
                <input
                  type="tel"
                  name="phone"
                  value={values.phone}
                  onChange={onChange}
                  className={inputClass}
                  autoComplete="tel"
                  placeholder="+252 61 1234567"
                />
              </label>
            </div>

            <div className="grid gap-5 sm:grid-cols-2">
              <label className="block">
                <span className={labelClass}>Date of birth</span>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={values.dateOfBirth}
                  onChange={onChange}
                  className={inputClass}
                />
              </label>

              <label className="block">
                <span className={labelClass}>Gender</span>
                <select name="gender" value={values.gender} onChange={onChange} className={inputClass}>
                  <option value="">Prefer not to say</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              </label>
            </div>

            <label className="block">
              <span className={labelClass}>Address</span>
              <textarea
                name="address"
                value={values.address}
                onChange={onChange}
                rows={3}
                className="w-full resize-y rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-maroon-600 focus:ring-2 focus:ring-maroon-600/20"
                placeholder="City, district, street"
              />
            </label>

            {submitError ? (
              <p className="flex items-start gap-2 text-sm text-red-600" role="alert">
                <Info className="mt-0.5 h-5 w-5 shrink-0" strokeWidth={ICON_STROKE} aria-hidden />
                <span>{submitError}</span>
              </p>
            ) : null}

            <button
              type="submit"
              disabled={submitting}
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-maroon-700 px-6 text-sm font-semibold text-white shadow-md shadow-maroon-900/25 transition hover:bg-maroon-800 disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {submitting ? (
                "Submitting…"
              ) : (
                <>
                  Submit application
                  <ArrowRight className="h-5 w-5" strokeWidth={ICON_STROKE} aria-hidden />
                </>
              )}
            </button>
          </form>
        </section>
      )}

      <aside className="space-y-6 lg:sticky lg:top-24">
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card">
          <h3 className="text-sm font-bold uppercase tracking-wide text-maroon-700">How it works</h3>
          <ol className="mt-4 space-y-4">
            {[
              {
                title: "Submit your application",
                body: "Tell us about yourself and choose your program and department.",
              },
              {
                title: "Our team reviews it",
                body: "The admissions office checks your details and eligibility.",
              },
              {
                title: "Get your decision",
                body: "We contact you with the outcome, fee, and class placement.",
              },
            ].map((step, i) => (
              <li key={step.title} className="flex gap-3">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-maroon-700 text-xs font-bold text-white">
                  {i + 1}
                </span>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{step.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="rounded-2xl border border-maroon-200 bg-maroon-50 p-6">
          <h3 className="text-sm font-bold text-maroon-800">Need help applying?</h3>
          <p className="mt-2 text-sm leading-relaxed text-maroon-900/80">
            Reach out to our admissions office if you have questions about programs, fees, or
            required documents.
          </p>
          <Link
            to="/#contact"
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-maroon-700 hover:text-maroon-800"
          >
            Contact admissions
            <ArrowRight className="h-4 w-4" strokeWidth={ICON_STROKE} aria-hidden />
          </Link>
        </div>
      </aside>
      </div>
    </main>
  );
}
